import ExcelJS from 'exceljs';

import { convertTableToCsvInput, exportCsvFromData } from '@utils/exportCsv';

import {
  defaultLeadTemplateHeaders,
  templateLeadDummyData
} from '../constants/leadBulkCreate';
import { LeadValidationLists } from '../types/leadBulkCreate';
import { getCities } from './common';

export const getDefaultLeadTemplateHeaders = (
  baseHeaders: string[] = defaultLeadTemplateHeaders
) => baseHeaders;

export const getTemplateLeadDummyData = (
  baseData: string[] = templateLeadDummyData
) => baseData;

const addListColumn = (
  validationSheet: ExcelJS.Worksheet,
  columnIndex: number,
  title: string,
  values: string[]
): string => {
  const columnLetter = validationSheet.getColumn(columnIndex).letter;
  // Write a section title in row 1,
  // then the values from row 2 in the given column
  validationSheet.getCell(`${columnLetter}1`).value = title;
  const items = (values || []).filter((v) => !!v);
  let cursor = 2;
  (items.length ? items : ['']).forEach((v) => {
    validationSheet.getCell(`${columnLetter}${cursor}`).value = v;
    cursor += 1;
  });
  // Return the usable range (excluding title row)
  const range = `Validation!$${columnLetter}$2:$${columnLetter}$${cursor - 1}`;
  return range;
};

const createLeadTemplateWithDropdowns = async (
  headers: string[],
  data: string[],
  lists: LeadValidationLists
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Template');
  const validationSheet = workbook.addWorksheet('Validation');
  validationSheet.state = 'hidden';

  // Write headers + sample data
  worksheet.addRow(headers);
  worksheet.addRow(data);

  // Build validation columns sequentially in Validation sheet
  const sections: Record<string, string> = {
    statuses: '',
    sources: '',
    tags: '',
    categories: '',
    priorities: '',
    assignees: '',
    cities: ''
  };

  // All cities as a single sorted list
  const allCities: string[] = getCities();
  const citiesRange = addListColumn(validationSheet, 1, 'cities', allCities);
  sections.cities = citiesRange;

  // Other flat lists continue after the cities column
  const nextCol = 2;
  const flatKeys: Array<keyof LeadValidationLists> = [
    'statuses',
    'sources',
    'tags',
    'categories',
    'priorities',
    'assignees'
  ];
  flatKeys.forEach((key, idx) => {
    const range = addListColumn(
      validationSheet,
      nextCol + idx,
      String(key),
      (lists[key] as string[]) || []
    );
    sections[String(key)] = range;
  });

  const addListValidation = (
    headerKey: string,
    listRange: string,
    errorTitle: string
  ) => {
    const colIndex = headers.indexOf(headerKey) + 1;
    if (colIndex <= 0) return;
    const columnLetter = worksheet.getColumn(colIndex).letter;
    worksheet.dataValidations.add(`${columnLetter}2:${columnLetter}101`, {
      type: 'list',
      formulae: [listRange],
      showErrorMessage: true,
      errorStyle: 'error',
      errorTitle,
      error: `Please select a value from the list`,
      allowBlank: true
    });
  };

  // Map headers to lists
  addListValidation('Status', sections.statuses, 'Invalid Status');
  addListValidation('Source', sections.sources, 'Invalid Source');
  addListValidation('Tags', sections.tags, 'Invalid Tag');
  addListValidation('Category', sections.categories, 'Invalid Category');
  addListValidation('Priority', sections.priorities, 'Invalid Priority');
  addListValidation('Assignee', sections.assignees, 'Invalid Assignee');
  addListValidation('City', sections.cities, 'Invalid City');

  // Format template headers and columns
  worksheet.columns = headers.map(() => ({ width: 20 }));
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length }
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = 'template.xlsx';
  link.click();
  window.URL.revokeObjectURL(url);
};

export const downloadLeadTemplateWithLists = async (
  lists: LeadValidationLists
) => {
  const headers = getDefaultLeadTemplateHeaders();
  const baseData = getTemplateLeadDummyData();
  return createLeadTemplateWithDropdowns(headers, baseData, lists);
};

export const downloadLeadCsvFromPreview = (headers: string[], rows: any[]) => {
  const CSVInputString = convertTableToCsvInput(headers, rows);
  exportCsvFromData(CSVInputString, 'lead-details');
};
